import React, { useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  MenuItem,
  Menu,
  Modal,
  Button,
  TextField,
} from '@mui/material';
import { Close as CloseIcon, MoreHoriz } from '@mui/icons-material';
import { renameSession, deleteSession } from '../../../IdeationService';
//import { fieldAPIKeyMap } from '../../../../NewIdeationXPLR/NewIdeation/constants';
import ShowToast, { ToastTypeEnum } from '../../../../../common/components/ShowToast';
import { useNavigate } from 'react-router-dom';
import {
  fieldAPIKeyMap,
  fieldConversationTypeMap,
} from '../../../../NewIdeationXPLR/shared/constants';

interface ChatHeaderProps {
  title: string;
  chatId: string;
  field: string;
  useCaseDetails: Record<string, any>;
  setuseCaseDetails: (details: Record<string, any>) => void;
  onRefetchSessions?: () => void;
  handleNewChat: () => void;
  conversationType?: string;
}

const ChatHeader = ({
  title,
  chatId,
  field,
  useCaseDetails,
  setuseCaseDetails,
  onRefetchSessions,
  handleNewChat,
  conversationType = '',
}: ChatHeaderProps) => {
  const navigate = useNavigate();

  const [sessionMenuAnchor, setSessionMenuAnchor] = useState<null | HTMLElement>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');

  // ── Session menu ──────────────────────────────────────────────
  const handleSessionMenuOpen = (e: React.MouseEvent<HTMLElement>) =>
    setSessionMenuAnchor(e.currentTarget);
  const handleSessionMenuClose = () => setSessionMenuAnchor(null);

  // ── Rename ────────────────────────────────────────────────────
  const handleRenameClick = () => {
    setNewSessionName(useCaseDetails?.title || '');
    setSessionMenuAnchor(null);
    setRenameDialogOpen(true);
  };

  const handleRenameSubmit = async () => {
    try {
      await renameSession(chatId, newSessionName, fieldAPIKeyMap[field], conversationType);
      setuseCaseDetails({ ...useCaseDetails, title: newSessionName });
      ShowToast({ message: 'Session renamed successfully', type: ToastTypeEnum.Success });
      setRenameDialogOpen(false);
      onRefetchSessions?.();
    } catch {
      ShowToast({ message: 'Error renaming session', type: ToastTypeEnum.Error });
    }
  };

  // ── Delete ────────────────────────────────────────────────────
  const handleDeleteClick = () => {
    setSessionMenuAnchor(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const conversation_type = fieldConversationTypeMap[field];
      await deleteSession(chatId, fieldAPIKeyMap[field], conversation_type);
      ShowToast({ message: 'Session deleted successfully', type: ToastTypeEnum.Success });
      setDeleteDialogOpen(false);
      onRefetchSessions?.();
      navigate('/data-xplr');
      handleNewChat();
    } catch {
      ShowToast({ message: 'Error deleting session', type: ToastTypeEnum.Error });
    }
  };

  return (
    <>
      {/* ── Header bar ── */}
      <Paper
        elevation={0}
        sx={{
          p: '12px',
          borderRadius: '12px 12px 0 0',
          borderBottom: '1px solid #E1E1E1',
          borderColor: 'divider',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                color: '#333333',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {title || 'AI Opportunity Discovery'}
            </Typography>

            <IconButton
              size="small"
              onClick={handleSessionMenuOpen}
              sx={{
                color: '#6b7280',
                padding: '4px',
                flexShrink: 0,
                '&:hover': { backgroundColor: '#e5e7eb' },
              }}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>

            <Menu
              anchorEl={sessionMenuAnchor}
              open={Boolean(sessionMenuAnchor)}
              onClose={handleSessionMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleRenameClick} sx={{ fontSize: '14px' }}>
                Rename
              </MenuItem>
              <MenuItem onClick={handleDeleteClick} sx={{ fontSize: '14px', color: '#d32f2f' }}>
                Delete
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Paper>

      {/* ── Delete modal ── */}
      <Modal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: 24,
            width: { xs: '90%', sm: 700 },
            maxWidth: 700,
            outline: 'none',
          }}
        >
          <ModalHeader title="Delete Ideation" onClose={() => setDeleteDialogOpen(false)} />

          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: '14px', color: '#333' }}>
              Do you want to delete this session?
            </Typography>
          </Box>

          <ModalActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ color: '#666', textTransform: 'none', fontSize: '14px' }}
            >
              No
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: '#1A55F9',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': { backgroundColor: '#1547D9' },
              }}
            >
              Yes
            </Button>
          </ModalActions>
        </Box>
      </Modal>

      {/* ── Rename modal ── */}
      <Modal
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: 24,
            width: { xs: '90%', sm: 700 },
            maxWidth: 700,
            outline: 'none',
          }}
        >
          <ModalHeader title="Rename Session" onClose={() => setRenameDialogOpen(false)} />

          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '14px', color: '#333', mb: 1.5 }}>
                Session name
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: newSessionName.trim().length > 100 ? '#EF4444' : '#6b7280',
                  fontFamily: 'Poppins',
                }}
              >
                {newSessionName.trim().length}/100
              </Typography>
            </Box>

            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              value={newSessionName}
              inputProps={{ maxLength: 100 }}
              onChange={(e) => setNewSessionName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newSessionName.trim()) handleRenameSubmit();
              }}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px' } }}
            />
          </Box>

          <ModalActions>
            <Button
              onClick={() => setRenameDialogOpen(false)}
              sx={{ color: '#666', textTransform: 'none', fontSize: '14px' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameSubmit}
              variant="contained"
              disabled={!newSessionName.trim()}
              sx={{
                backgroundColor: '#1A55F9',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': { backgroundColor: '#1547D9' },
                '&.Mui-disabled': { backgroundColor: '#B8C5F0', color: '#fff' },
              }}
            >
              Rename
            </Button>
          </ModalActions>
        </Box>
      </Modal>
    </>
  );
};

// ── Small shared sub-components used only inside modals ──────────
const ModalHeader = ({ title, onClose }: { title: string; onClose: () => void }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      p: 2,
      backgroundColor: '#1A55F9',
      borderRadius: '8px 8px 0 0',
    }}
  >
    <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#fff' }}>{title}</Typography>
    <IconButton
      size="small"
      onClick={onClose}
      sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

const ModalActions = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 1,
      p: 2,
      borderTop: '1px solid #E5E5E5',
    }}
  >
    {children}
  </Box>
);

export default ChatHeader;
